import { Control } from '@yleisradio/areena-types';
import React from 'react';
import { isActivator } from '../typeGuards';
import styles from './SidebarItem.module.scss';
import { getTranslation } from '../localizations/translations';
import { getAssetIconWithFallback } from './getIcons';
import { useProfilesLocalStorage } from '../ProfilesLocalStorageContext';
import ReactRouterLink from '../Routes/ReactRouterLink';
import { cls } from '../View/utils';
import { ProfileIconButton } from '../common/ProfileIconButton/ProfileIconButton';
import { useAppFeaturesContext } from '../contexts/AppFeaturesContext/AppFeaturesContext';
import { useAuthContext } from '../contexts/AuthContext/AuthContext';

export const isProfileActivator = (control: Control) =>
  isActivator(control) && control?.functionality.id === 'profile';

const ProfileItem = ({
  title,
  ariaDescription,
}: {
  title?: string;
  ariaDescription: string;
}) => {
  const { user } = useAuthContext();

  const { profiles } = useProfilesLocalStorage();
  const profile = profiles.find(p => p.userId === user?.userId);
  const initials = user?.initials;
  const { languageCode } = useAppFeaturesContext();
  const changeProfileText = getTranslation(
    'sidebar_changeProfile',
    languageCode
  );

  const iconClasses = cls({
    [styles.sidebarProfileIcon]: true,
    [styles.sideBarProfileIconSubAccounts]: true,
  });

  return (
    <>
      <span
        className={iconClasses}
        data-testid="sidebar-profile-icon"
        aria-label={`${user?.displayName || initials}, ${changeProfileText}, ${ariaDescription}`}
      >
        {profile && (
          <ProfileIconButton
            icon={profile.icon}
            imageAlt={getTranslation('sidebar_profileAvatar', languageCode)}
            text={initials}
            textSize="s"
          />
        )}
      </span>
      <span className={styles.sidebarItemTitle} aria-hidden="true">
        {profiles.length > 1 ? changeProfileText : title}
      </span>
    </>
  );
};

type ItemProps = {
  active: boolean;
  control: Control;
  ariaDescription: string;
};

const Item = ({ active, control, ariaDescription }: ItemProps) => {
  const LocalIcon = getAssetIconWithFallback(control, active);

  const itemIconClasses = cls({
    [styles.sidebarItemIcon]: true,
    [styles.sidebarItemIconActive]: active,
  });

  return (
    <>
      {LocalIcon && (
        <span className={itemIconClasses}>
          <LocalIcon />
        </span>
      )}
      <span
        className={styles.sidebarItemTitle}
        aria-label={`${control.title}, ${ariaDescription}`}
      >
        {control.title}
      </span>
    </>
  );
};

type Props = {
  control: Control;
  id: string;
  index: number;
  totalCount: number;
};

const SidebarItem = ({ control, id, index, totalCount }: Props) => {
  const isProfileItem = isProfileActivator(control);
  const { languageCode } = useAppFeaturesContext();

  const getItemElement = (
    control: Control,
    active: boolean,
    ariaDescription: string
  ) =>
    isProfileItem ? (
      <ProfileItem title={control.title} ariaDescription={ariaDescription} />
    ) : (
      <Item
        active={active}
        control={control}
        ariaDescription={ariaDescription}
      />
    );

  const ariaDescription = `${getTranslation('sidebarTextsForScreenreader_link', languageCode)} ${index + 1}, ${getTranslation('sidebarTextsForScreenreader_menuTotal', languageCode)} ${totalCount} ${getTranslation('sidebarTextsForScreenreader_links', languageCode)}`;

  return (
    <ReactRouterLink
      pointer={control.destination}
      functionality={control.functionality}
      className={styles.sidebarMenuItem}
      id={id}
      dataTestId={`menu-item-${control.icon?.id || control.title}`}
    >
      {({ isActive }) => getItemElement(control, isActive, ariaDescription)}
    </ReactRouterLink>
  );
};

export default SidebarItem;
